import React, { useCallback } from 'react';
import { Button } from '@gafe/common-ui';
import merge from 'classnames';

import { CollapsibleCard } from '@/components/generic/CollapsibleCard';
import { WarningPopOver } from '@/components/generic/WarningPopOver';
import { ContentCard } from '@/components/layout/ContentCard';
import {
  getStepByName,
  markStepAsCompleted,
  useOrderContext,
  useOrderDispatch,
} from '@/components/modals/SubmitOrder/helpers';

import { StepsName } from '../../types';
import { ProposalDetails } from '../Common/ProposalDetails';

import { EligibilityCheck } from './components/EligibilityCheck';
import { LegalInformation } from './components/LegalInformation';
import { getEligibilityValidationErrors } from './helpers';
import { Props } from './types';

import modalStyles from '../../SubmitOrderModal.module.scss';
import styles from './EligibilityStep.module.scss';

export const EligibilityStep = ({ goToStep, close, stepName }: Props) => {
  const state = useOrderContext();
  const dispatch = useOrderDispatch();
  const orderDetailsStep = getStepByName(state, StepsName['Order Details']);

  const errors = getEligibilityValidationErrors(state);
  const isNextStepDisabled = errors.length > 0;

  const updateOrderByField = useCallback(
    (field: string, value: unknown): void => {
      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          [field]: value,
        },
      });
    },
    [dispatch],
  );

  const goToOrderDetails = useCallback(() => {
    goToStep?.(orderDetailsStep);
    markStepAsCompleted(state.wizardHeaderSteps, stepName);
  }, [goToStep, orderDetailsStep, state.wizardHeaderSteps, stepName]);

  return (
    <div>
      <ProposalDetails
        orderItems={state.visibleOrderItems}
        purchaseNumber={state.order.purchaseNumber}
        shouldDisplayPurchaseNumber
      />

      <div className={styles.block_wrapper}>
        <CollapsibleCard
          classes={{
            wrapper: styles.message_board_card,
            header: styles.message_board_card_header,
          }}
          title="Eligibility Check"
        >
          <ContentCard
            className={merge(
              styles.no_shadow_content_card_variant,
              styles.message_board_card_content,
            )}
            type="light"
          >
            <EligibilityCheck updateOrderByField={updateOrderByField} />
          </ContentCard>
        </CollapsibleCard>
      </div>
      <div className={styles.block_wrapper}>
        <CollapsibleCard
          classes={{
            wrapper: styles.message_board_card,
            header: styles.message_board_card_header,
          }}
          title="Legal Information"
        >
          <ContentCard
            className={merge(
              styles.no_shadow_content_card_variant,
              styles.message_board_card_content,
            )}
            type="light"
          >
            <LegalInformation
              isChecked={Boolean(state.order.legalComplianceConfirmed)}
              onToggle={value => updateOrderByField('legalComplianceConfirmed', value)}
            />
          </ContentCard>
        </CollapsibleCard>
      </div>
      <div className={modalStyles.stepper_buttons}>
        <Button colorSchema="neutral" onClick={close} size="large" variant="text">
          Cancel
        </Button>
        <WarningPopOver productErrors={errors}>
          <Button
            colorSchema="primary"
            disabled={isNextStepDisabled}
            onClick={goToOrderDetails}
            size="large"
          >
            Continue
          </Button>
        </WarningPopOver>
      </div>
    </div>
  );
};
